import { useState } from 'react';
import { Copy, KeyRound } from 'lucide-react';
import type { PasswordResetLink } from '@ste/shared';
import { Banner, Button } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { CommandError } from '../charging/CommandError.tsx';

/** STE staff create a one-time link for someone who forgot the password (ADR-0015) */
export function ResetLinkPanel({ create }: { create(resetTotp: boolean): Promise<PasswordResetLink> }) {
  const [resetTotp, setResetTotp] = useState(false);
  const [result, setResult] = useState<PasswordResetLink>();
  const link = result?.link;
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function submit() {
    setBusy(true);
    setError(undefined);
    setCopied(false);
    try {
      setResult(await create(resetTotp));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 w-full space-y-2 rounded-xl border border-border bg-surface-2 p-3">
      <label className="flex min-h-11 items-center gap-3 text-sm">
        <input
          type="checkbox"
          className="h-5 w-5 accent-[var(--accent)]"
          checked={resetTotp}
          onChange={(e) => setResetTotp(e.target.checked)}
        />
        {t('reset.lostPhone')}
      </label>
      <Button variant="secondary" disabled={busy} onClick={() => void submit()}>
        <KeyRound size={16} /> {t('reset.createButton')}
      </Button>
      {error !== undefined && <CommandError error={error} />}
      {link && (
        <Banner
          tone="accent"
          title={result?.emailedTo ? t('mail.sentTo', { email: result.emailedTo }) : t('reset.created')}
        >
          <span className="block break-all">{link}</span>
          <button
            type="button"
            className="mt-1 inline-flex min-h-11 items-center gap-1 text-accent-text"
            onClick={() => void navigator.clipboard?.writeText(link).then(() => setCopied(true))}
          >
            <Copy size={14} /> {copied ? t('team.copied') : t('team.copy')}
          </button>
        </Banner>
      )}
    </div>
  );
}
